/* eslint-disable max-len */
import { Col, Row, message } from 'antd';
import { Formik } from 'formik';
import { isEmpty } from 'lodash/fp';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import * as permissionKind from '../../../../../shared/permissions';
import {
    type DealerTranslatedStringSettingDataFragment,
    type GiftVoucherModuleInDealerSpecsFragment,
} from '../../../../api/fragments';
import { type DealerWithPermissionsFragmentFragment } from '../../../../api/fragments/DealerWithPermissionsFragment';
import { type GiftVoucherModuleEmailContentsSpecsFragment } from '../../../../api/fragments/GiftVoucherModuleEmailContentsSpecs';
import { useUpdateGiftVoucherModuleEmailContentMutation } from '../../../../api/mutations/updateGiftVoucherModuleEmailContent';
import { EmailContentUpdateType, type TranslatedStringInput } from '../../../../api/types';
import Form from '../../../../components/fields/Form';
import TranslatedTextAreaField from '../../../../components/fields/TranslatedTextAreaField';
import CollapsibleWrapper, { Panel } from '../../../../components/wrappers/CollapsibleWrapper';
import hasPermissions from '../../../../utilities/hasPermissions';
import useHandleError from '../../../../utilities/useHandleError';

export type DealerAssignmentGiftVoucherFormProps = {
    module: GiftVoucherModuleInDealerSpecsFragment;
    dealer: DealerWithPermissionsFragmentFragment;
};

type DealerGiftVoucherEmailInput = {
    subject?: TranslatedStringInput;
    introTitle?: TranslatedStringInput;
    contentText?: TranslatedStringInput;
};

type FormValues = {
    customer: {
        confirmation: DealerGiftVoucherEmailInput;
    };
};

const getEmailContentItem = (item: DealerTranslatedStringSettingDataFragment, dealerId: string) =>
    item.overrides.find(item => item.dealerId === dealerId)?.value ?? {
        defaultValue: '',
        overrides: [],
    };

const getEmailContent = (
    emailContent: Omit<GiftVoucherModuleEmailContentsSpecsFragment, '__typename'>,
    dealerId: string
) => ({
    subject: getEmailContentItem(emailContent.customer.confirmation.subject, dealerId),
    introTitle: getEmailContentItem(emailContent.customer.confirmation.introTitle, dealerId),
    contentText: getEmailContentItem(emailContent.customer.confirmation.contentText, dealerId),
});

const addInputToOverride = (
    dealerId: string,
    originEmailContentItem: DealerTranslatedStringSettingDataFragment,
    translatedInput: TranslatedStringInput
) => ({
    ...originEmailContentItem,
    overrides: [
        ...originEmailContentItem.overrides.filter(item => item.dealerId !== dealerId),
        !isEmpty(translatedInput.defaultValue) && {
            dealerId,
            value: translatedInput,
        },
    ].filter(Boolean),
});

const setEmailItem = (
    dealerId: string,
    input: DealerGiftVoucherEmailInput,
    originEmailContent: Omit<GiftVoucherModuleEmailContentsSpecsFragment, '__typename'>
) => ({
    subject: addInputToOverride(dealerId, originEmailContent.customer.confirmation.subject, input.subject),
    introTitle: addInputToOverride(dealerId, originEmailContent.customer.confirmation.introTitle, input.introTitle),
    contentText: addInputToOverride(dealerId, originEmailContent.customer.confirmation.contentText, input.contentText),
});

type DealerGiftVoucherEmailFieldProps = {
    path: 'customer.confirmation' | 'salesPerson.confirmation';
    children?: React.ReactNode;
    disabled?: boolean;
};

const DealerGiftVoucherEmailFields = ({ path, children, disabled }: DealerGiftVoucherEmailFieldProps) => {
    const { t } = useTranslation('giftVoucherModuleDetails');

    const getName = useCallback((name: string) => `${path}.${name}`, [path]);
    const getTranslationKey = useCallback((name: string) => `giftVoucherModuleDetails:fields.emails.${name}`, []);

    return (
        <Row gutter={10}>
            <Col lg={8} xs={24}>
                <TranslatedTextAreaField
                    {...t(getTranslationKey('subject'), { returnObjects: true })}
                    autoSize={{ minRows: 2, maxRows: 6 }}
                    disabled={disabled}
                    name={getName('subject')}
                />
            </Col>
            <Col lg={8} xs={24}>
                <TranslatedTextAreaField
                    {...t(getTranslationKey('introTitle'), { returnObjects: true })}
                    autoSize={{ minRows: 2, maxRows: 6 }}
                    disabled={disabled}
                    name={getName('introTitle')}
                />
            </Col>
            <Col lg={8} xs={24}>
                <TranslatedTextAreaField
                    {...t(getTranslationKey('contentText'), { returnObjects: true })}
                    autoSize={{ minRows: 2, maxRows: 6 }}
                    disabled={disabled}
                    name={getName('contentText')}
                    withContentRefinement
                />
            </Col>
            {children}
        </Row>
    );
};

const DealerAssignmentGiftVoucherForm = ({ module, dealer }: DealerAssignmentGiftVoucherFormProps) => {
    const { t } = useTranslation(['dealershipManagement', 'giftVoucherModuleDetails', 'moduleDetails']);

    const [updateGiftVoucherModuleEmailContent] = useUpdateGiftVoucherModuleEmailContentMutation();

    // initialize finance products and dealers for the dealer
    const initialValues: FormValues = useMemo(
        () => ({
            customer: {
                confirmation: getEmailContent(module.emailContents, dealer.id),
            },
        }),
        [dealer.id, module.emailContents]
    );

    const onSubmit = useHandleError<FormValues>(
        async values => {
            const { customer } = values;

            // submitting message
            message.loading({
                content: t('dealershipManagement:messages.updateSubmitting'),
                key: 'primary',
                duration: 0,
            });

            await updateGiftVoucherModuleEmailContent({
                variables: {
                    settings: {
                        emailContentUpdateType: EmailContentUpdateType.Dealer,
                        dealerId: dealer.id,
                        customer: {
                            confirmation: setEmailItem(dealer.id, customer.confirmation, module.emailContents),
                        },
                    },
                    moduleId: module.id,
                },
            });

            // show successful message
            message.success({
                content: t('dealershipManagement:messages.updateSuccessful'),
                key: 'primary',
            });
        },
        [t, updateGiftVoucherModuleEmailContent, dealer.id, module.emailContents, module.id]
    );

    const formId = `dealerForm-${module.id}`;
    const hasUpdatePermission = hasPermissions(dealer.permissions, [permissionKind.updateDealer]);

    return (
        <Formik<FormValues> initialValues={initialValues} onSubmit={onSubmit} enableReinitialize>
            {({ handleSubmit }) => (
                <Form id={formId} name={formId} onSubmitCapture={handleSubmit}>
                    <CollapsibleWrapper defaultActiveKey={['emailContent']}>
                        <Panel key="emailContent" header={t('giftVoucherModuleDetails:tabs.email.title')}>
                            <CollapsibleWrapper defaultActiveKey={['emailContentsCustomerConfirmation']}>
                                <Panel
                                    key="emailContentsCustomerConfirmation"
                                    header={t('giftVoucherModuleDetails:sections.emails.customer.confirmation')}
                                >
                                    <DealerGiftVoucherEmailFields
                                        disabled={!hasUpdatePermission}
                                        path="customer.confirmation"
                                    />
                                </Panel>
                            </CollapsibleWrapper>
                        </Panel>
                    </CollapsibleWrapper>
                </Form>
            )}
        </Formik>
    );
};

export default DealerAssignmentGiftVoucherForm;
